import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

import TitleWithBack from '../../Home/TitleWithBack';
import StorageFactory from '../../../Util/StorageFactory';
import {
  ROUTE_ENQUIRY_DETAILS,
  SESSION_USER_COUNTRY_CODE_KEY,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY
} from '../../../Util/VMConstants';
import {
  API_CREATE_INVOICE,
  API_GET_BOOKED_ROOMS,
  API_GET_ENQUIRY_INFO
} from '../../../Util/URLConstants';
import {
  applyCatalogServicesToEvents,
  getServicesList,
  groupServicesBySubVenue,
} from '../../../Services/additionalServicesService';

import InvoiceUserinfo from './InvoiceUserinfo';
import VenueBilling from './VenueBilling';
import FoodBilling from './FoodBilling';
import RoomBilling from './RoomBilling';
import AdditionalServicesBilling from './AdditionalServicesBilling';
import Loader from '../../../Components/Loader';
import CompanyLogoUploader from './CompanyLogoUploader';
import TermsConditions from './TermsConditions';
import PaymentsSection from './PaymentsSection';
import PaymentMethodSection from './PaymentMethodSection';
import InvoiceSummary from './InvoiceSummary';
import { isEmpty } from '../../../Util/UtilHelper';
import { VM_Colors } from '../../../Styles/VMStyles';

const PAYMENT_OPTIONS = ['Cash', 'Card', 'Online', 'UPI', 'Others'];

function VMCreateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const enquiry_id = location?.state?.enquiry_id || null;

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const countryCode = sessionFactory.get(SESSION_USER_COUNTRY_CODE_KEY);
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  /* ---------------- STATES ---------------- */

  const [venueState, setVenueState] = useState(null);
  const [enquiryState, setEnquiryState] = useState(null);
  const [bookedEventState, setBookedEventState] = useState([]);
  const [bookedRoomsState, setBookedRoomsState] = useState([]);

  // Summary (lifted)
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [taxList, setTaxList] = useState([{ name: 'GST', percentage: 18 }]);

  const [terms, setTerms] = useState([]);
  const [newPaymentOption] = useState('');
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('');

  const [state, setState] = useState({
    loading: false,
    advance_amount: '',
    pending_amount: '',
  });

  /* ---------------- API CALLS ---------------- */

  const getUserInfo = useCallback(() => {
    if (!userID || !enquiry_id) return;
    setState(prev => ({ ...prev, loading: true }));
    Promise.all([
      axios.get(`${API_GET_ENQUIRY_INFO}?user_id=${userID}&enquiry_id=${enquiry_id}`),
      getServicesList({ user_id: userID }).catch(() => ({ data: [] })),
    ])
      .then(([resp, catalogResult]) => {
        const data = resp.data;
        setVenueState(data.venue_info);
        setEnquiryState(data.enquiry_info);
        const groupedCatalog = groupServicesBySubVenue(catalogResult?.data || []);
        setBookedEventState(
          applyCatalogServicesToEvents(data.booked_info || [], groupedCatalog)
        );
      })
      .finally(() => setState(prev => ({ ...prev, loading: false })));
  }, [enquiry_id, userID]);

  const getBookedRoomsInfo = useCallback(() => {
    if (!userID || !enquiry_id) return;
    axios
      .get(`${API_GET_BOOKED_ROOMS}?user_id=${userID}&enquiry_id=${enquiry_id}`)
      .then(resp => setBookedRoomsState(resp.data?.data?.rooms_info || []));
  }, [enquiry_id, userID]);

  useEffect(() => {
    getUserInfo();
    getBookedRoomsInfo();
  }, [getBookedRoomsInfo, getUserInfo]);

  /* ---------------- UPDATE HANDLERS ---------------- */

  const updateVenueBillingHandler = (index, field, value) => {
    setBookedEventState(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const updateFoodBillingHandler = (eventIdx, menuIdx, field, value) => {
    setBookedEventState(prev =>
      prev.map((event, i) => {
        if (i !== eventIdx) return event;

        const updatedMenus = event.dining_details.map((menu, j) => {
          if (j !== menuIdx) return menu;

          const plates = Number(field === 'no_of_plates' ? value : menu.no_of_plates || 0);
          const cost = Number(field === 'cost_per_plate' ? value : menu.cost_per_plate || 0);

          return {
            ...menu,
            [field]: value,
            total_menu_cost: plates * cost
          };
        });

        const dining_total_menu_cost = updatedMenus.reduce(
          (sum, m) => sum + (Number(m.total_menu_cost) || 0),
          0
        );

        return { ...event, dining_details: updatedMenus, dining_total_menu_cost };
      })
    );
  };

  const updateAdditionalServiceHandler = useCallback((eventIdx, serviceIdx, field, value) => {
    setBookedEventState(prev =>
      prev.map((event, i) => {
        if (i !== eventIdx) return event;
        const services = [...(event.service_details || [])];
        if (serviceIdx < 0 || serviceIdx >= services.length) return event;
        const next = { ...services[serviceIdx] };
        if (field === 'service_charge') {
          const sanitized = String(value ?? '').replace(/[^\d.]/g, '');
          next.service_charge = sanitized;
        } else if (field === 'service_name') {
          next.service_name = value;
        }
        services[serviceIdx] = next;
        return { ...event, service_details: services };
      })
    );
  }, []);

  /* ---------------- SUMMARY CALCULATIONS ---------------- */

  const totalVenueCharges = bookedEventState.reduce(
    (a, i) => a + (Number(i.event_rent_charges) || 0),
    0
  );

  const totalFoodCharges = bookedEventState.reduce(
    (a, i) => a + (Number(i.dining_total_menu_cost) || 0),
    0
  );

  const totalRoomCharges = bookedRoomsState.reduce(
    (a, r) => a + (Number(r.booked_room_cost) || 0),
    0
  );

  const totalPersonCharges = bookedEventState.reduce((acc, event) => {
    const persons = event.accomodation_persons_data?.data || [];
    return acc + persons.reduce((pAcc, p) => pAcc + (Number(p.cost) || 0), 0);
  }, 0);

  const totalAdditionalServices = bookedEventState.reduce((acc, event) => {
    const services = event.service_details || [];
    return acc + services.reduce(
      (sAcc, s) => sAcc + (Number(s.service_charge) || 0),
      0
    );
  }, 0);

  const totalAmount =
    totalVenueCharges +
    totalFoodCharges +
    totalRoomCharges +
    totalPersonCharges +
    totalAdditionalServices;

  let discountedTotal = totalAmount;
  if (discountType === 'percentage') {
    discountedTotal -= (totalAmount * Number(discountValue || 0)) / 100;
  } else {
    discountedTotal -= Number(discountValue || 0);
  }
  if (discountedTotal < 0) discountedTotal = 0;

  const totalTaxAmount = taxEnabled
    ? taxList.reduce(
        (acc, tax) => acc + discountedTotal * (tax.percentage / 100),
        0
      )
    : 0;

  const finalPayableAmount = discountedTotal + totalTaxAmount;

  useEffect(() => {
    const pendingAmount =
      parseFloat(finalPayableAmount) - parseFloat(state.advance_amount || 0);
    setState((prev) => ({
      ...prev,
      pending_amount: pendingAmount >= 0 ? pendingAmount.toFixed(2) : '0.00'
    }));
  }, [finalPayableAmount, state.advance_amount]);


  /* ---------------- CREATE INVOICE ---------------- */

  const handleCreateInvoice = () => {
    const parsedAdvance = Number(state.advance_amount);

    if (isEmpty(state.advance_amount ) || Number.isNaN(parsedAdvance) ||  parsedAdvance <= 0 ) {
      toast.error("Enter Advance Amount To Continue");
      return;
    }

    if (!selectedPaymentOption) {
      toast.error('Please select payment method');
      return;
    }

    const reqbody = {
      user_id: userID,
      enquiry_id,
      total_venue_charges: totalVenueCharges,
      total_food_charges: totalFoodCharges,
      total_room_charges: totalRoomCharges,
      total_person_charges: totalPersonCharges,
      total_additional_services: totalAdditionalServices,
      grand_total_amount: totalAmount,
      discount_type: discountType,
      discount_value: Number(discountValue || 0),
      total_discount: totalAmount - discountedTotal,
      tax_list: taxList,
      final_payable_amount: finalPayableAmount,
      advance_amount: parsedAdvance,
      pending_amount: finalPayableAmount - parsedAdvance,
      payment_mode:
        selectedPaymentOption === 'Others'
          ? newPaymentOption.trim()
          : selectedPaymentOption,
      terms_conditions: terms,
      venue_info: venueState,
      events_info: bookedEventState,
      rooms_info: bookedRoomsState,
      enquiry_info: enquiryState,
    };

    setState(prev => ({ ...prev, loading: true }));

    axios
      .post(API_CREATE_INVOICE, reqbody)
      .then(res => {
        setState(prev => ({ ...prev, loading: false }));
        if (res.data.status_code === 200) {
          toast.success(res.data.message);
          navigate(`/${ROUTE_ENQUIRY_DETAILS}`, {
            replace: true,
            state: { enquiry_id, navigate_from: 'invoice' }
          });
        } else {
          toast.error(res.data.message);
        }
      })
      .catch(() => {
        setState(prev => ({ ...prev, loading: false }));
        toast.error('Invoice creation failed');
      });
  };

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2, md: 4 },
        pb: 10,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <TitleWithBack title="Create Invoice" onBackClick={() => navigate(-1)} />

      {enquiryState && (
        <InvoiceUserinfo
          enquiryState={enquiryState}
          venueState={venueState}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            flex: { md: '1 1 58%' },
            minWidth: 0,
            maxWidth: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <VenueBilling bookedEventState={bookedEventState} updateVenueBillingHandler={updateVenueBillingHandler} />
          <FoodBilling bookedEventState={bookedEventState} updateFoodBillingHandler={updateFoodBillingHandler} />
          {bookedRoomsState.length > 0 && <RoomBilling bookedRoomsState={bookedRoomsState} />}
          <AdditionalServicesBilling
            bookedEventState={bookedEventState}
            updateAdditionalServiceHandler={updateAdditionalServiceHandler}
          />
           <PaymentMethodSection
            selected={selectedPaymentOption}
            setSelected={setSelectedPaymentOption}
            options={PAYMENT_OPTIONS}
          />
          <PaymentsSection state={state} setState={setState} countryCode={countryCode} />
       
          <TermsConditions terms={terms} setTerms={setTerms} />
         
        </Box>

        <Box
          sx={{
            flex: { md: '1 1 38%' },
            minWidth: 0,
            maxWidth: '100%',
            width: '100%',
            mt:4
          }}
        >
          <CompanyLogoUploader enquiry_id={enquiry_id} />
          <Box sx={{ mt: 4 }}>

          <InvoiceSummary
            currencySymbol={currencySymbol}
            totalVenueCharges={totalVenueCharges}
            totalFoodCharges={totalFoodCharges}
            totalRoomCharges={totalRoomCharges}
            totalPersonCharges={totalPersonCharges}
            totalAdditionalServices={totalAdditionalServices}
            totalAmount={totalAmount}
            discountedTotal={discountedTotal}
            finalPayableAmount={finalPayableAmount}
            discountType={discountType}
            discountValue={discountValue}
            setDiscountType={setDiscountType}
            setDiscountValue={setDiscountValue}
            taxEnabled={taxEnabled}
            setTaxEnabled={setTaxEnabled}
            taxList={taxList}
            setTaxList={setTaxList}
          />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: VM_Colors.white,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          rowGap: 1.5,
          zIndex: 10,
          boxSizing: 'border-box',
        }}
      >
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreateInvoice}>
          Create Invoice
        </Button>
      </Box>

      <Loader loading={state.loading} />
    </Box>
  );
}

export default VMCreateInvoice;